import { useCallback } from 'react'

import { useApolloClient } from '@apollo/client'
import { useQueryClient } from '@tanstack/react-query'

import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'

import {
  ArchiveCodeBookItemMutation,
  ArchiveCodeBookItemMutationVariables,
  CodeBookItemsByIdsQuery,
  CodeBookItemsByIdsQueryVariables,
  CreateCodeBookItemMutation,
  CreateCodeBookItemMutationVariables,
  SearchCodeBookItemsQuery,
  SearchCodeBookItemsQueryVariables,
  UpdateCodeBookItemMutation,
  UpdateCodeBookItemMutationVariables,
} from '../../types/graphql'
import { QueryKey, useMutation, useQuery } from '../utils/queryClient'
const CreateMutation = gql`
  mutation CreateCodeBookItemMutation($input: CreateCodeBookItemInput!) {
    createCodeBookItem(input: $input) {
      id
    }
  }
`

export const useCreateCodeBookItemMutation = () => {
  return useMutation<CreateCodeBookItemMutation, CreateCodeBookItemMutationVariables>({
    mutationDocument: CreateMutation,
  })
}

const UpdateMutation = gql`
  mutation UpdateCodeBookItemMutation($id: Int!, $input: UpdateCodeBookItemInput!) {
    updateCodeBookItem(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateCodeBookItemMutation = () => {
  return useMutation<UpdateCodeBookItemMutation, UpdateCodeBookItemMutationVariables>({
    mutationDocument: UpdateMutation,
  })
}

const ArchiveMutation = gql`
  mutation ArchiveCodeBookItemMutation($id: Int!) {
    archiveCodeBookItem(id: $id) {
      id
    }
  }
`

export const useArchiveCodeBookItemMutation = () => {
  return useMutation<ArchiveCodeBookItemMutation, ArchiveCodeBookItemMutationVariables>({
    mutationDocument: ArchiveMutation,
  })
}

const Search = gql`
  query SearchCodeBookItemsQuery($query: String!, $organizationId: Int!) {
    searchCodeBookItems(query: $query, organizationId: $organizationId) {
      ...CodeBookItemOptionFragment
    }
  }
`
const GetByIds = gql`
  query CodeBookItemsByIdsQuery($input: CodeBookItemsByIdsInput!) {
    codeBookItemsByIds(input: $input) {
      ...CodeBookItemFragment
    }
  }
`

/**
 * Resolves code book items by id without archive filtering. Use for displaying
 * already-referenced items (e.g. saved form field values) so they keep their
 * labels even after their book is archived.
 */
export const useCodeBookItemsByIdsQuery = (ids: number[], options?: { enabled?: boolean }) => {
  const organizationId = useMyOrgId()
  const enabled = (options?.enabled ?? true) && !!organizationId && ids.length > 0
  const sortedIds = [...ids].sort((a, b) => a - b)

  return useQuery<CodeBookItemsByIdsQuery, CodeBookItemsByIdsQueryVariables>({
    queryKey: [QueryKey.CodeBookItemsByIds, organizationId, sortedIds],
    queryDocument: GetByIds,
    variables: { input: { organizationId, ids } },
    enabled,
  })
}

export const useSearchCodeBookItemsQuery = () => {
  const organizationId = useMyOrgId()
  const queryClient = useQueryClient()
  const client = useApolloClient()
  return useCallback(
    (query: string) => {
      if (!organizationId) {
        return Promise.resolve({ searchCodeBookItems: [] } as SearchCodeBookItemsQuery)
      }
      return queryClient.fetchQuery<SearchCodeBookItemsQuery, SearchCodeBookItemsQueryVariables>({
        queryKey: [QueryKey.CodeBookItems, query, organizationId],
        queryFn: ({ signal }) =>
          client
            .query<SearchCodeBookItemsQuery, SearchCodeBookItemsQueryVariables>({
              query: Search,
              variables: {
                query: query || '',
                organizationId,
              },
              context: {
                fetchOptions: {
                  signal,
                },
              },
            })
            .then(({ data }) => data),
      })
    },
    [client, queryClient, organizationId]
  )
}
